import type {
  OrpheusConsent,
  OrpheusConsentAdmin,
  OrpheusConsentStatusDto,
  OrpheusConsentUser,
  OrpheusConsentUserDto,
} from '../models/orpheusConsent.types'

import { ApiError } from '@/shared/http'

/**
 * The backend lists every account-scoped admin, which includes Orphex's own sysadmin user on
 * accounts it was added to — not someone a customer can ask to turn Orpheus on (v3
 * `WORKSPACE_INSIGHTS_SYSADMIN_EMAIL`).
 */
const ORPHEX_SYSADMIN_EMAIL = 'sysadmin@orphex.co'

export const ORPHEUS_CONSENT_REQUIRED_CODE = 'ORPHEUS_CONSENT_REQUIRED'

function toUser(dto: OrpheusConsentUserDto | null | undefined): OrpheusConsentUser | null {
  const email = typeof dto?.email === 'string' ? dto.email : ''
  const name = typeof dto?.name === 'string' && dto.name.trim() !== '' ? dto.name : email
  return name === '' ? null : { name, email }
}

/** The status, read defensively: anything but a literal `true` is "off". */
export function toOrpheusConsent(dto: OrpheusConsentStatusDto | null | undefined): OrpheusConsent {
  const given = dto?.given === true
  return {
    given,
    grantedAt: given && typeof dto?.granted_at === 'string' ? dto.granted_at : null,
    grantedBy: given ? toUser(dto?.granted_by) : null,
  }
}

/** The people a member can ask to turn Orpheus on, without Orphex's own sysadmin. */
export function contactableAdmins(admins: unknown): OrpheusConsentUser[] {
  if (!Array.isArray(admins)) return []
  return admins
    .map((admin) => toUser(admin as OrpheusConsentUserDto))
    .filter(
      (admin): admin is OrpheusConsentUser =>
        admin !== null && admin.email.toLowerCase() !== ORPHEX_SYSADMIN_EMAIL,
    )
}

function initialsOf(admin: OrpheusConsentUser): string {
  const source = (admin.name || admin.email).trim()
  const parts = source.split(/\s+/).filter(Boolean)
  const first = parts[0] ?? ''
  const last = parts[parts.length - 1] ?? ''
  const letters = parts.length > 1 ? `${first.charAt(0)}${last.charAt(0)}` : source.slice(0, 2)
  return letters.toLocaleUpperCase('tr-TR')
}

/**
 * The backend lists the account owner first, then every account-scoped admin
 * (`common/orpheus_consent.py::account_admin_users`) — there is no role field on the wire, so
 * position is the only signal for "Account owner" vs "Account admin".
 */
export function presentAdmins(admins: OrpheusConsentUser[]): OrpheusConsentAdmin[] {
  return admins.map((admin, index) => ({
    ...admin,
    initials: initialsOf(admin),
    role: index === 0 ? 'owner' : 'admin',
  }))
}

function envelope(error: ApiError): Record<string, unknown> | null {
  const body = error.payload
  const raw = body && typeof body === 'object' ? (body as Record<string, unknown>).error : null
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
}

export interface OrpheusConsentRequired {
  callerRole: 'owner' | 'admin' | 'member'
  accountAdmins: OrpheusConsentUser[]
}

/** A chat/ask call refused because the account turned Orpheus off (403), or null. */
export function orpheusConsentRequired(cause: unknown): OrpheusConsentRequired | null {
  const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  if (error.status !== 403 || error.code !== ORPHEUS_CONSENT_REQUIRED_CODE) return null
  const raw = envelope(error)?.details
  const info = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const role = info.caller_role
  return {
    callerRole: role === 'owner' || role === 'admin' ? role : 'member',
    accountAdmins: contactableAdmins(info.account_admins),
  }
}

export type OrpheusConsentProbeResult =
  { off: true; accountAdmins: OrpheusConsentUser[] } | { off: false } | null

/**
 * Reads what a member's empty-body feature-chat ask came back with. The ask endpoint checks
 * consent before anything else — before credits, rate limit and body validation — so the empty
 * body never reaches the model: 403 ORPHEUS_CONSENT_REQUIRED means off, the 400 for the missing
 * fields (or a 402 out of credits) means consent passed. Anything else is unknown.
 */
export function readConsentProbe(cause: unknown): OrpheusConsentProbeResult {
  const required = orpheusConsentRequired(cause)
  if (required) return { off: true, accountAdmins: required.accountAdmins }
  const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  return error.status === 400 || error.status === 402 ? { off: false } : null
}
